"use client";

import { ReactElement, useEffect, useRef } from "react";
import moment from "moment-timezone";
import styles from "./styles/calendar.module.scss";
import classNames from "classnames";
import { CSSTransition } from "react-transition-group";
import { IReactiveEventContentItem } from "../../schoolfeed/SchoolFeedContentItem";
import { MonthCalendar } from "./MonthCalendar";
import {
  currentDateFormat,
  ICurrentRange,
  useCleverCalendarData,
} from "./utils";
import { ListCalendar } from "./ListCalendar";

const transitionClassNames = {
  enter: styles.transitionEnter,
  enterActive: styles.transitionEnterActive,
  enterDone: styles.transitionEnterDone,
  exit: styles.transitionExit,
  exitActive: styles.transitionExitActive,
  exitDone: styles.transitionExitDone,
};

export default function CleverCalendar(props: {
  events: IReactiveEventContentItem[];
  onRangeChange: (range: ICurrentRange) => void;
  aboveTheFold: boolean;
  expandedView?: boolean;
  loading?: boolean;
  handleDeleteEvent: (contentItem: ContentItem) => void;
  initialDate?: string;
  viewMoreButton?: ReactElement;
}) {
  const { currentMonth, currentDate, nextMonth, prevMonth, setCurrentDate } =
    useCleverCalendarData(props.onRangeChange, props.initialDate);

  const transitionRefEvents = useRef(null);
  const transitionRefCalendar = useRef(null);

  useEffect(() => {
    const firstEventOnCurrentDate: HTMLLIElement | null =
      document.querySelector(
        `#events-container li[data-start-date="${currentDate.format("MMM-D-YYYY")}"]`,
      );
    if (firstEventOnCurrentDate) {
      const scrollableList = document.querySelector("#events-container");
      if (scrollableList) {
        scrollableList.scroll({
          top: firstEventOnCurrentDate.offsetTop,
          behavior: "smooth",
        });
      }
    }
  }, [currentDate, props.aboveTheFold, props.events.length]);

  const contentContainerClassName = classNames({
    [styles.contentContainer]: true,
  });
  const contentTransitionClassName = classNames({
    [styles.contentTransition]: true,
    [styles.contentTransitionHeightConstrained]: !props.expandedView,
  });

  return (
    <div className={styles.container}>
      <div className={contentContainerClassName}>
        <CSSTransition
          nodeRef={transitionRefEvents}
          appear
          in={props.aboveTheFold}
          timeout={500}
          classNames={transitionClassNames}
        >
          <div
            ref={transitionRefEvents}
            className={contentTransitionClassName}
            id={"events-container"}
          >
            <ListCalendar
              currentMonth={currentMonth}
              currentDate={currentDate.format(currentDateFormat)}
              setCurrentDate={(date) =>
                setCurrentDate(moment(date, currentDateFormat))
              }
              events={props.events}
              prevMonth={prevMonth}
              nextMonth={nextMonth}
              handleDeleteEvent={props.handleDeleteEvent}
            />
          </div>
        </CSSTransition>
      </div>

      <div className={contentContainerClassName}>
        <CSSTransition
          nodeRef={transitionRefCalendar}
          appear
          in={props.aboveTheFold}
          timeout={500}
          classNames={transitionClassNames}
        >
          <div
            ref={transitionRefCalendar}
            className={`${contentTransitionClassName} ${styles.eventListContainer}`}
          >
            <MonthCalendar
              currentMonth={currentMonth}
              currentDate={currentDate.format(currentDateFormat)}
              setCurrentDate={(date) =>
                setCurrentDate(moment(date, currentDateFormat))
              }
              events={props.events}
              prevMonth={prevMonth}
              nextMonth={nextMonth}
            />
          </div>
        </CSSTransition>
      </div>
    </div>
  );
}
